import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import { api } from 'src/common/utils/api';
import { UploadedFile } from './types';

export type OneDriveFileUploadInput = {
  items: PickedItem[];
  location?: string;
  modelId?: string;
  documentDate?: string | null;
  parentId?: string | null;
  isResolved?: boolean;
  isTemporary?: boolean;
};

type Props = {
  clientId?: string;
};

export const useOneDriveFileUpload = ({ clientId: overrideClientId = undefined }: Props = {}) => {
  const { clientId: currentClientId } = useCurrentClient();
  const clientId = overrideClientId ?? currentClientId;
  const queryClient = useQueryClient();
  const resource = `clients/${clientId}/data-upload-files/onedrive`;
  const fileListKey = ['clients', clientId, 'data-upload-files'];

  const mutation = useMutation({
    mutationFn: async (input: OneDriveFileUploadInput): Promise<UploadedFile[]> => {
      const files = input.items
        .filter(item => item.downloadUrl)
        .map(item => ({
          fileName: item.name || 'Unknown',
          downloadUrl: item.downloadUrl!,
          fileSize: item.size,
        }));

      const { data } = await api.post<UploadedFile[]>(resource, {
        files,
        modelId: input.modelId,
        documentDate: input.documentDate?.split('T')[0],
        parentId: input.parentId,
        isTemporary: input.isTemporary,
        isResolved: input.isResolved,
      });

      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fileListKey });
      await queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'all-documents'] });
    },
  });

  return {
    uploadOneDriveFiles: mutation.mutateAsync,
    isUploading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
};
